/**
 * Adding a new official account? Update all three files AND re-seed Soko,
 * or Soko will call the new account unofficial.
 * The three files: src/config/site.js, backend/app/contacts.py and soko/backend/contacts.py.
 *
 * The one place brand text, the canonical host and contact details live.
 *
 * Every page title, meta description, footer line and structured-data block
 * reads from here. The Netlify edge function keeps its own copy of the same
 * few constants, because it runs on Deno and cannot import this module;
 * netlify/edge-functions/seo.js says so at the point it declares them.
 * Nothing else may hard-code brand wording — the whole reason this file
 * exists is that one category-specific line of copy was pasted into 17 files
 * and reached Google as the description of a general marketplace.
 *
 * **The wording below is approved copy. Do not rewrite it**, including
 * punctuation: BRAND_DESCRIPTION, META_DESCRIPTION and HOME_TITLE are quoted
 * word for word from the brief, and a test asserts index.html still matches.
 *
 * **A null contact value means "not confirmed yet", and null must HIDE the
 * thing rather than print a placeholder.** The site previously shipped an
 * address on a domain we do not own, and an invented toll-free number, to
 * real customers, and both reached Google's search results. Absent beats
 * wrong: an Organization with no contactPoint is valid structured data, an
 * Organization with a fake one is a false claim about a business.
 */

export const SITE_NAME = "Emporia";

// www is canonical — decided, measured, and recorded in CLAUDE.md. The apex
// and emporia.top 301 here with the path preserved. Never a trailing slash:
// canonicalUrl() adds the one the root needs.
export const BASE_URL = "https://www.emporia.ke";

export const HOME_TITLE = "Emporia – Kenya's Online Marketplace | Pay with M-Pesa";

export const BRAND_DESCRIPTION =
  "Emporia is a Kenyan online marketplace offering a wide variety of products across " +
  "fashion, electronics, home and living, beauty, accessories, and more. Customers can " +
  "discover products from local sellers, pay securely through M-Pesa, and enjoy " +
  "convenient delivery across Kenya.";

export const META_DESCRIPTION =
  "Shop fashion, electronics, home products, beauty, accessories and more on Emporia, " +
  "a Kenyan online marketplace with secure M-Pesa payments and nationwide delivery.";

// The square 512px icon, not the 2,066 KB source. Google reads this for the
// Organization logo and wants something it can actually use; the old value
// pointed at a 948x1432 file that was also (wrongly) the favicon.
export const LOGO_URL = `${BASE_URL}/brand/icon-512.png`;
// The card every scraper shows for a shared link. Dated in the filename
// because a social card is cached hard by WhatsApp, Facebook and Slack —
// changing the bytes behind a stable URL can leave the old image showing for
// weeks, while a new URL is fetched immediately.
//
// public/og-default.png is KEPT, deliberately: links already shared point at
// it, and deleting it would turn those into a broken image. It is no longer
// referenced by anything. Its artwork names a single clothing category the
// business outgrew, which is why it was replaced.
export const OG_IMAGE = `${BASE_URL}/og-default-2026-09.png`;

// ── Contact — CONFIRMED by the owner, 2026-09-27 ─────────────────────────
// Three forms of one number, because they are read by different things and
// must not be improvised at the call site:
//   PHONE_DISPLAY  what a person reads
//   PHONE          E.164, for structured data and anything machine-read
//   PHONE_HREF     the tel: link
//
// 0180 is one of the ranges Safaricom opened on 10 August 2026 — see
// src/utils/phone.js for why no validation rule anywhere may be written as
// a list of operator prefixes. Emporia's own number would fail such a rule.
//
// Hours CONFIRMED 2026-09-27: 24/7. Shown next to the number wherever it
// appears, because a number with no stated hours makes a caller guess, and
// "24/7" is the single most useful thing to say next to one.
//
// PHONE_HOURS is the display string; PHONE_HOURS_SCHEMA is the
// schema.org OpeningHoursSpecification the Organization contactPoint
// carries. 23:59 rather than 24:00 because schema.org closing times are
// wall-clock on the same day.
//
// SUPPORT_EMAIL is TEMPORARY (owner, 2026-10-11): the help desk runs from an
// Outlook mailbox while emporia.ke's mail moves from Zoho to Microsoft 365.
// Swap it to customerservice@emporia.ke once Microsoft 365 is live — this
// line, backend/app/contacts.py and soko/backend/contacts.py, together.
// src/config/contacts.test.js fails if the three disagree.
export const SUPPORT_EMAIL = "emporiake.helpdesk@outlook.com";
export const SUPPORT_EMAIL_HREF = `mailto:${SUPPORT_EMAIL}`;
export const PHONE_DISPLAY = "0180 191 887";
export const PHONE = "+254180191887";
export const PHONE_HREF = "tel:+254180191887";
export const PHONE_HOURS = "24/7";
export const PHONE_HOURS_SCHEMA = {
  "@type": "OpeningHoursSpecification",
  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  opens: "00:00",
  closes: "23:59",
};

export const ADDRESS_LOCALITY = "Nairobi";
export const ADDRESS_COUNTRY = "KE";
export const CITY_LINE = "Nairobi, Kenya";

// The OFFICIAL accounts — confirmed by the owner, 2026-10-11, and the only
// ones. Soko tells customers any other account claiming to be Emporia is not
// us, so this list is also what it reads from (soko/backend/contacts.py
// holds the same list; contacts.test.js keeps them identical).
//
// Real profile URLs only. The footer once linked https://www.instagram.com
// and four other bare domain roots; as `sameAs` entries those are worse than
// nothing, because sameAs is a claim that the URL is this organisation's own
// profile. `handle` is what a person reads (null for Facebook: a numeric
// profile.php URL has no handle, and inventing one would be a claim nobody
// checked); `href` is exact, with no tracking query (TikTok's ?lang=en was
// dropped on purpose).
export const SOCIAL_PROFILES = [
  { label: "Facebook", handle: null, href: "https://www.facebook.com/profile.php?id=61594966573997" },
  { label: "Instagram", handle: "@emporia.ke", href: "https://www.instagram.com/emporia.ke/" },
  { label: "TikTok", handle: "@emporia.ke", href: "https://www.tiktok.com/@emporia.ke" },
  { label: "X", handle: "@emporiakenya", href: "https://x.com/emporiakenya" },
  { label: "Threads", handle: "@emporia.ke", href: "https://www.threads.com/@emporia.ke" },
];

/**
 * The canonical URL for a path: always the www host, never a query string.
 *
 * Query strings are stripped because `?utm_source=…`, `?fbclid=…`, `?sort=…`
 * and every filter combination are the same page to a search engine. A
 * canonical that carried them would split one page's signals across endless
 * near-duplicate URLs — and a self-referencing canonical that does not match
 * the indexed URL is worse than none.
 *
 * Trailing slash: the root keeps its "/", every other path has none. One
 * style, used here, in the sitemap and in the edge function, because two
 * styles are two URLs for one page.
 */
export const canonicalUrl = (pathname = "/") => {
  const path = String(pathname).split("?")[0].split("#")[0];
  if (path === "" || path === "/") return `${BASE_URL}/`;
  return `${BASE_URL}${path.replace(/\/+$/, "")}`;
};

/** Organization + WebSite, the shape every page's JSON-LD graph starts from. */
export const organizationSchema = () => {
  const org = {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    name: SITE_NAME,
    // What people actually search for. "Emporia" alone is a common word and
    // a US city; "Emporia Kenya" is how someone looking for this business
    // types it, and alternateName is where that belongs.
    alternateName: `${SITE_NAME} Kenya`,
    url: BASE_URL,
    logo: { "@type": "ImageObject", url: LOGO_URL },
    description: BRAND_DESCRIPTION,
    // On the Organization as well as the contactPoint: the contactPoint's
    // areaServed says where the PHONE LINE serves, this says where the
    // business does.
    areaServed: ADDRESS_COUNTRY,
    address: {
      "@type": "PostalAddress",
      addressCountry: ADDRESS_COUNTRY,
      addressLocality: ADDRESS_LOCALITY,
    },
    // Only profiles that genuinely exist. sameAs is a CLAIM that the URL is
    // this organisation's own profile, so a bare domain root (the network's
    // home page) is worse than an omission — five of those were removed for
    // exactly that reason.
    sameAs: SOCIAL_PROFILES.map((s) => s.href),
  };
  // The guard stays: if either value is ever cleared, the field disappears
  // rather than becoming a half-filled contactPoint.
  if (SUPPORT_EMAIL || PHONE) {
    org.contactPoint = {
      "@type": "ContactPoint",
      contactType: "customer service",
      ...(SUPPORT_EMAIL ? { email: SUPPORT_EMAIL } : {}),
      ...(PHONE ? { telephone: PHONE } : {}),
      areaServed: "KE",
      availableLanguage: "English",
      ...(PHONE && PHONE_HOURS_SCHEMA ? { hoursAvailable: PHONE_HOURS_SCHEMA } : {}),
    };
  }
  return org;
};

export const websiteSchema = () => ({
  "@type": "WebSite",
  "@id": `${BASE_URL}/#website`,
  url: BASE_URL,
  name: SITE_NAME,
  publisher: { "@id": `${BASE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

/** Home › Category › Product, for category and product pages. */
export const breadcrumbSchema = (crumbs) => ({
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: canonicalUrl(c.path),
  })),
});

const siteConfig = {
  SITE_NAME, BASE_URL, HOME_TITLE, BRAND_DESCRIPTION, META_DESCRIPTION,
  LOGO_URL, OG_IMAGE, SUPPORT_EMAIL, SUPPORT_EMAIL_HREF,
  PHONE, PHONE_DISPLAY, PHONE_HREF, PHONE_HOURS, PHONE_HOURS_SCHEMA,
  ADDRESS_LOCALITY, ADDRESS_COUNTRY,
  CITY_LINE, SOCIAL_PROFILES, canonicalUrl, organizationSchema, websiteSchema,
  breadcrumbSchema,
};

export default siteConfig;
